// frontend/src/pages/SharedSession.js
// Public view for shared sessions (link-based sharing)

import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Container,
  Card,
  CardContent,
  Avatar,
  Button,
  Stack,
  Collapse,
  Snackbar,
  Divider,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Menu,
  MenuItem,
  ListItemIcon,
  TextField
} from '@mui/material';
import {
  Tab as TabIcon,
  Error as ErrorIcon,
  OpenInNew as OpenAllIcon,
  Language as DomainIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarToday as CalendarIcon,
  OpenInNew as ExternalIcon,
  SaveAlt as SaveIcon,
  Extension as ExtensionIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  ViewModule as ViewModuleIcon,
  Code as CodeIcon,
  NoteAdd as NoteAddIcon,
  MoreVert as MoreVertIcon,
  AutoAwesome as AutoAwesomeIcon,
  CallSplit as CallSplitIcon,
  CompareArrows as CompareArrowsIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from '../api/axiosConfig';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { detectBrowser, getExtensionUrl, getBrowserLabel } from '../config/extensionUrls';
import { generateMarkdown } from '../utils/sessionMarkdown';
import { normalizeUrlForDiff } from '../utils/urlNormalize';
import { slugify } from '../utils/shareUrl';

// Inline markdown renderer for tab descriptions — NO raw HTML, inline-only
const inlineMarkdownComponents = {
  p: ({ children }) => <span>{children}</span>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
      style={{ color: 'inherit', textDecoration: 'underline' }}>
      {children}
    </a>
  ),
  code: ({ children }) => (
    <code style={{
      backgroundColor: 'rgba(0,0,0,0.06)',
      padding: '1px 4px',
      borderRadius: 3,
      fontSize: '0.85em',
      fontFamily: 'monospace'
    }}>
      {children}
    </code>
  ),
  // Block-level elements stripped to prevent layout issues
  h1: ({ children }) => <strong>{children}</strong>,
  h2: ({ children }) => <strong>{children}</strong>,
  h3: ({ children }) => <strong>{children}</strong>,
  blockquote: ({ children }) => <span>{children}</span>,
  img: () => null,
};

/** Renders a tab description, using ReactMarkdown for markdown-format descriptions */
const TabDescription = ({ tab, sx = {} }) => {
  const hasDesc = tab.description && tab.description.trim() !== (tab.title || '').trim();
  if (!hasDesc) return null;

  if (tab.descriptionFormat === 'markdown') {
    return (
      <Typography variant="body2" color="text.secondary" component="div" sx={{
        lineHeight: 1.5,
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        '& > span': { display: 'inline' },
        ...sx
      }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={inlineMarkdownComponents}>
          {tab.description}
        </ReactMarkdown>
      </Typography>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary" sx={{
      lineHeight: 1.5,
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      ...sx
    }}>
      {tab.description}
    </Typography>
  );
};

const SharedSession = () => {
  const theme = useTheme();
  const params = useParams();
  const location = useLocation();

  // Two access patterns:
  // 1. Via React Router route: /shared/session/:token/:slug? → params.token exists
  // 2. Via direct render (bypasses Routes): /@username/shareSlug → parse from pathname
  const token = params.token || null;
  const slug = params.slug || null;
  let username = null;
  let shareSlug = null;
  if (!token && location.pathname.startsWith('/@')) {
    const cleanPathname = location.pathname.replace(/\/+$/, ''); // strip trailing slashes
    const parts = cleanPathname.split('/').filter(Boolean);
    const rawUsername = parts[0]?.slice(1) || ''; // strip "@"
    try { username = rawUsername ? decodeURIComponent(rawUsername) : null; } catch { username = rawUsername || null; }
    try { shareSlug = parts[1] ? decodeURIComponent(parts[1]) : null; } catch { shareSlug = parts[1] || null; }
  }
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [session, setSession] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedDomains, setCollapsedDomains] = useState({});
  const [cloning, setCloning] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isStarred, setIsStarred] = useState(false);
  const [starCount, setStarCount] = useState(0);
  const [starLoading, setStarLoading] = useState(false);
  const [viewMode, setViewMode] = useState('cards');
  const [generatingTabDescriptions, setGeneratingTabDescriptions] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [forking, setForking] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [parentTabs, setParentTabs] = useState(null);
  const [loadingDiff, setLoadingDiff] = useState(false);
  // Issue 2: AI description styles
  const [descriptionStyle, setDescriptionStyle] = useState('brief');
  // Issue 4: Editable description
  const [editingDescription, setEditingDescription] = useState(false);
  const [descDraft, setDescDraft] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);
  // Tab description style (separate from session description style)
  const [tabDescriptionStyle, setTabDescriptionStyle] = useState('brief');
  // Inline tab description editing
  const [editingTabId, setEditingTabId] = useState(null);
  const [tabDescDraft, setTabDescDraft] = useState('');
  const [savingTabDesc, setSavingTabDesc] = useState(false);
  // Issue 6: Typography font mode
  const [fontMode, setFontMode] = useState('sans');
  // Curated-list: collapsible sections + search
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [showSecondary, setShowSecondary] = useState({});

  useEffect(() => {
    const fetchSharedSession = async () => {
      try {
        setLoading(true);
        // Issue 7: Support both token-based and slug-based URLs
        const url = token
          ? `/api/shared/session/${token}`
          : `/api/shared/by-slug/${username}/${shareSlug}`;
        const response = await axios.get(url);
        const sessionData = response.data.session;
        setSession(sessionData);
        setTabs(response.data.tabs || []);
        setOwner(response.data.owner || null);
        setIsStarred(sessionData?.isStarredByViewer || false);
        setStarCount(sessionData?.starCount || 0);

        // Issue 7: Upgrade legacy URL to clean URL via replaceState
        if (token && sessionData?.canonicalPath) {
          const canonical = sessionData.canonicalPath.replace(/\/+$/, '');
          window.history.replaceState(null, '', canonical);
        }
      } catch (err) {
        console.error('Error fetching shared session:', err);
        setError(err.response?.data?.error || 'Failed to load shared session');
      } finally {
        setLoading(false);
      }
    };

    if (token || (username && shareSlug)) {
      fetchSharedSession();
    } else {
      // No valid access pattern — bad URL like /@, /@/, /@/slug, partial params, etc.
      setError('Invalid share link. Expected format: /@username/session-name');
      setLoading(false);
    }
  }, [token, username, shareSlug]);

  // Pretty URL slug via replaceState (only for legacy token-based URLs without slug)
  useEffect(() => {
    if (!token || !session?.name || slug) return;
    // Skip if already upgraded to canonical path
    if (window.location.pathname.startsWith('/@')) return;
    const nameSlug = slugify(session.name);
    if (!nameSlug) return;
    window.history.replaceState(null, '', `/shared/session/${token}/${nameSlug}`);
  }, [session, token, slug]);

  // Group tabs by domain (for non-section view)
  const domainGroups = useMemo(() => {
    const groups = {};
    tabs.forEach(tab => {
      let domain = tab.hostname || 'Other';
      if (domain === 'Other') {
        try { domain = new URL(tab.url).hostname || 'Other'; } catch {}
      }
      if (!groups[domain]) {
        groups[domain] = [];
      }
      groups[domain].push(tab);
    });
    // Sort domains by tab count (descending)
    return Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
  }, [tabs]);

  const uniqueDomains = domainGroups.length;

  // Check if session has sections
  const hasSections = session?.sections?.length > 0;
  const isCuratedList = session?.displayMode === 'curated-list';

  // Filter tabs by search query
  const filteredTabs = useMemo(() => {
    if (!searchQuery.trim()) return tabs;
    const q = searchQuery.toLowerCase();
    return tabs.filter(t =>
      (t.title || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q) ||
      (t.hostname || '').toLowerCase().includes(q)
    );
  }, [tabs, searchQuery]);

  // Group tabs by section (for section view)
  const sectionGroups = useMemo(() => {
    if (!hasSections) return [];
    const sections = session.sections || [];
    const sectionOrder = [...sections].sort((a, b) => a.order - b.order);
    const groups = [];
    const sectionTabMap = {};
    const activeTabs = searchQuery.trim() ? filteredTabs : tabs;

    // Initialize groups for each section
    for (const sec of sectionOrder) {
      sectionTabMap[sec.id] = [];
    }
    const unsectioned = [];

    // Distribute tabs into sections
    for (const tab of activeTabs) {
      if (tab.sectionId && sectionTabMap[tab.sectionId]) {
        sectionTabMap[tab.sectionId].push(tab);
      } else {
        unsectioned.push(tab);
      }
    }

    // Build ordered groups, separating primary from secondary
    for (const sec of sectionOrder) {
      const sectionTabs = sectionTabMap[sec.id];
      if (sectionTabs.length > 0) {
        const primary = isCuratedList ? sectionTabs.filter(t => t.linkRole !== 'secondary') : sectionTabs;
        const secondary = isCuratedList ? sectionTabs.filter(t => t.linkRole === 'secondary') : [];
        groups.push({ id: sec.id, title: sec.title, tabs: primary, secondaryTabs: secondary });
      }
    }

    // Add unsectioned tabs at the end
    if (unsectioned.length > 0) {
      const primary = isCuratedList ? unsectioned.filter(t => t.linkRole !== 'secondary') : unsectioned;
      const secondary = isCuratedList ? unsectioned.filter(t => t.linkRole === 'secondary') : [];
      groups.push({ id: '__other', title: 'Other', tabs: primary, secondaryTabs: secondary });
    }

    return groups;
  }, [tabs, filteredTabs, session, hasSections, isCuratedList, searchQuery]);

  // Initialize collapsed sections once on first render — collapse all except first 3
  const [sectionsInitialized, setSectionsInitialized] = useState(false);
  useEffect(() => {
    if (isCuratedList && sectionGroups.length > 3 && !sectionsInitialized) {
      const toCollapse = new Set(sectionGroups.slice(3).map(g => g.id));
      setCollapsedSections(toCollapse);
      setSectionsInitialized(true);
    }
  }, [isCuratedList, sectionGroups, sectionsInitialized]);

  // Issue 3: Two markdown memos — view (no header) and export (full)
  const markdownForView = useMemo(
    () => session && tabs.length ? generateMarkdown(session, tabs, owner, { skipHeader: true }) : '',
    [session, tabs, owner]
  );
  const markdownForExport = useMemo(
    () => session && tabs.length ? generateMarkdown(session, tabs, owner) : '',
    [session, tabs, owner]
  );

  const handleCopyMarkdown = async () => {
    try {
      await navigator.clipboard.writeText(markdownForExport);
      setSnackbar({ open: true, message: 'Markdown copied to clipboard!', severity: 'success' });
    } catch {
      // Fallback for browsers without clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = markdownForExport;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      setSnackbar({
        open: true,
        message: success ? 'Markdown copied to clipboard!' : 'Failed to copy',
        severity: success ? 'success' : 'error'
      });
    }
    setExportMenuAnchor(null);
  };

  const handleDownloadMarkdown = () => {
    const blob = new Blob([markdownForExport], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slugify(session?.name) || 'session'}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setExportMenuAnchor(null);
  };

  const handleSaveAsNote = async () => {
    setExportMenuAnchor(null);
    if (!currentUser) {
      navigate('/login?next=' + encodeURIComponent(window.location.pathname));
      return;
    }
    setSavingNote(true);
    try {
      await axios.post('/api/notes', {
        title: session?.name || 'Shared Session',
        content: markdownForExport,
        format: 'markdown'
      });
      setSnackbar({ open: true, message: 'Saved as note!', severity: 'success' });
    } catch (err) {
      console.error('Failed to save as note:', err);
      setSnackbar({ open: true, message: 'Failed to save as note', severity: 'error' });
    } finally {
      setSavingNote(false);
    }
  };

  const MAX_OPEN_TABS = 50;

  const openAllTabs = () => {
    const tabsToOpen = tabs.slice(0, MAX_OPEN_TABS);
    tabsToOpen.forEach(tab => {
      window.open(tab.url, '_blank');
    });
  };

  const cloneSession = async () => {
    if (!currentUser) {
      navigate('/login?next=' + encodeURIComponent(window.location.pathname));
      return;
    }
    setCloning(true);
    try {
      await axios.post(`/api/shared/session/${session.shareToken}/clone`);
      setSnackbar({ open: true, message: 'session_saved', severity: 'success' });
    } catch (err) {
      console.error('Failed to clone session:', err);
      setSnackbar({ open: true, message: 'Failed to save session', severity: 'error' });
    } finally {
      setCloning(false);
    }
  };

  const handleGenerateTabDescriptions = async () => {
    if (!currentUser || !session?._id) return;
    setEditingTabId(null); // Clear any active inline edit
    setGeneratingTabDescriptions(true);
    try {
      await axios.post(`/api/v2/sessions/${session._id}/generate-tab-descriptions`, { overwriteAI: true, style: tabDescriptionStyle });
      // Re-fetch to get updated tab descriptions
      const refetchUrl = session.shareToken
        ? `/api/shared/session/${session.shareToken}`
        : `/api/shared/by-slug/${username}/${shareSlug}`;
      const response = await axios.get(refetchUrl);
      setTabs(response.data.tabs || []);
      setSnackbar({ open: true, message: 'Tab descriptions generated!', severity: 'success' });
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        setSnackbar({ open: true, message: 'Rate limit reached — try again later', severity: 'warning' });
      } else {
        console.error('Failed to generate tab descriptions:', err);
        setSnackbar({ open: true, message: 'Failed to generate descriptions', severity: 'error' });
      }
    } finally {
      setGeneratingTabDescriptions(false);
    }
  };

  const handleGenerateSessionDescription = async () => {
    if (!currentUser || !session?._id) return;
    setGeneratingDescription(true);
    try {
      const response = await axios.post(`/api/v2/sessions/${session._id}/generate-description`, {
        style: descriptionStyle
      });
      setSession(prev => ({ ...prev, description: response.data.description }));
      setSnackbar({ open: true, message: 'Session description generated!', severity: 'success' });
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        setSnackbar({ open: true, message: 'Rate limit reached — try again later', severity: 'warning' });
      } else {
        console.error('Failed to generate session description:', err);
        setSnackbar({ open: true, message: 'Failed to generate description', severity: 'error' });
      }
    } finally {
      setGeneratingDescription(false);
    }
  };

  // Issue 4: Save edited description
  const handleSaveDescription = async () => {
    if (!session?._id) return;
    setSavingDescription(true);
    try {
      await axios.patch(`/api/v2/sessions/${session._id}`, { description: descDraft });
      setSession(prev => ({ ...prev, description: descDraft }));
      setEditingDescription(false);
      setSnackbar({ open: true, message: 'Description saved!', severity: 'success' });
    } catch (err) {
      console.error('Failed to save description:', err);
      setSnackbar({ open: true, message: 'Failed to save description', severity: 'error' });
    } finally {
      setSavingDescription(false);
    }
  };

  // Inline tab description editing — save via PATCH
  const handleSaveTabDescription = async (tabId) => {
    if (!tabId) return;
    setSavingTabDesc(true);
    try {
      await axios.patch(`/api/v2/sessions/${session._id}/tabs/${tabId}`, {
        description: tabDescDraft,
        descriptionSource: 'user'
      });
      setTabs(prev => prev.map(t =>
        t.tabId === tabId ? { ...t, description: tabDescDraft, descriptionSource: 'user' } : t
      ));
      setEditingTabId(null);
      setSnackbar({ open: true, message: 'Tab description saved!', severity: 'success' });
    } catch (err) {
      console.error('Failed to save tab description:', err);
      setSnackbar({ open: true, message: 'Failed to save tab description', severity: 'error' });
    } finally {
      setSavingTabDesc(false);
    }
  };

  const forkSession = async () => {
    if (!currentUser) {
      navigate('/signup?next=' + encodeURIComponent(window.location.pathname));
      return;
    }
    setForking(true);
    try {
      await axios.post(`/api/shared/session/${session.shareToken}/fork`);
      setSession(prev => ({ ...prev, forkCount: (prev?.forkCount || 0) + 1 }));
      setSnackbar({ open: true, message: 'session_forked', severity: 'success' });
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        const forkId = err.response?.data?.existingFork?._id;
        setSnackbar({ open: true, message: 'already_forked', severity: 'info', forkId });
      } else if (status === 400) {
        setSnackbar({ open: true, message: 'Cannot fork your own session', severity: 'warning' });
      } else {
        console.error('Failed to fork session:', err);
        setSnackbar({ open: true, message: 'Failed to fork session', severity: 'error' });
      }
    } finally {
      setForking(false);
    }
  };

  const loadDiff = async () => {
    if (!session?.forkedFrom?.shareToken) return;
    if (parentTabs) { setShowDiff(!showDiff); return; }
    setLoadingDiff(true);
    try {
      const res = await axios.get(`/api/shared/session/${session.forkedFrom.shareToken}`);
      setParentTabs(res.data.tabs || []);
      setShowDiff(true);
    } catch {
      setSnackbar({ open: true, message: 'Original session is no longer available', severity: 'warning' });
    } finally {
      setLoadingDiff(false);
    }
  };

  // Diff computation
  const diffResult = useMemo(() => {
    if (!parentTabs || !showDiff) return null;
    const parentMap = new Map();
    for (const t of parentTabs) {
      parentMap.set(normalizeUrlForDiff(t.url), t);
    }
    const currentMap = new Map();
    for (const t of tabs) {
      currentMap.set(normalizeUrlForDiff(t.url), t);
    }

    const added = [];
    const removed = [];
    const edited = [];

    for (const [key, tab] of currentMap) {
      if (!parentMap.has(key)) {
        added.push(tab);
      } else {
        const parentTab = parentMap.get(key);
        if ((tab.description || '') !== (parentTab.description || '') ||
            (tab.title || '') !== (parentTab.title || '')) {
          edited.push({ current: tab, parent: parentTab });
        }
      }
    }
    for (const [key, tab] of parentMap) {
      if (!currentMap.has(key)) {
        removed.push(tab);
      }
    }

    return { added, removed, edited };
  }, [parentTabs, tabs, showDiff]);

  const handleToggleStar = async () => {
    if (!currentUser) {
      navigate('/signup?next=' + encodeURIComponent(window.location.pathname));
      return;
    }
    if (starLoading || !session?._id) return;
    setStarLoading(true);

    const wasStarred = isStarred;
    // Optimistic update
    setIsStarred(!wasStarred);
    setStarCount(prev => wasStarred ? Math.max(0, prev - 1) : prev + 1);

    try {
      await axios.patch(`/api/sessions/${session._id}/star`, {
        starred: !wasStarred,
        clientTs: new Date().toISOString()
      });
    } catch (err) {
      // Revert on failure
      console.error('Star toggle failed:', err);
      setIsStarred(wasStarred);
      setStarCount(prev => wasStarred ? prev + 1 : Math.max(0, prev - 1));
      setSnackbar({ open: true, message: 'Failed to update star', severity: 'error' });
    } finally {
      setStarLoading(false);
    }
  };

  const toggleDomain = (domain) => {
    setCollapsedDomains(prev => ({ ...prev, [domain]: !prev[domain] }));
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getFaviconUrl = (hostname) => {
    if (!hostname) return null;
    return `https://www.google.com/s2/favicons?domain=${hostname}&sz=32`;
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', gap: 2 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">Loading session...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Card sx={{ overflow: 'hidden' }}>
          <Box sx={{ height: 6, background: `linear-gradient(90deg, ${theme.palette.error.main}, ${theme.palette.error.dark})` }} />
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <ErrorIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>Session Not Found</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/')}>Go to Blackmount</Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  const seoTitle = session?.name ? `${session.name} — Blackmount` : 'Shared Session — Blackmount';
  const seoDescription = (() => {
    if (!session) return 'A shared browser session on Blackmount.';
    const tabCount = tabs.length;
    const domains = [...new Set(tabs.map(t => { try { return new URL(t.url).hostname; } catch { return null; } }).filter(Boolean))];
    const domainPreview = domains.slice(0, 3).join(', ');
    const suffix = domains.length > 3 ? ` and ${domains.length - 3} more` : '';
    let desc = `${tabCount} tab${tabCount !== 1 ? 's' : ''} across ${domains.length} site${domains.length !== 1 ? 's' : ''}${domainPreview ? ': ' + domainPreview + suffix : ''}`;
    if (owner?.username) desc = `Curated by @${owner.username} · ${desc}`;
    return desc;
  })();
  const seoCanonical = username && shareSlug
    ? `https://app.blackmount.ai/@${username}/${shareSlug}`
    : token
      ? `https://app.blackmount.ai/shared/session/${token}`
      : window.location.href;

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 3, md: 6 }, mb: 6, fontFamily: 'Inter, sans-serif' }}>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={seoCanonical} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoCanonical} />
        <meta property="og:image" content="https://app.blackmount.ai/og-image.png" />
        <meta property="og:site_name" content="Blackmount" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content="https://app.blackmount.ai/og-image.png" />
      </Helmet>

      {/* Session Header Card */}
      <Card sx={{ mb: 3, overflow: 'hidden', '&:hover': { transform: 'none' } }}>
        {/* Gradient accent bar */}
        <Box sx={{
          height: 6,
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
        }} />

        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          {/* Title + buttons */}
          <Stack
            direction="column"
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Typography variant="h5" fontWeight={700} sx={{ color: 'text.primary', lineHeight: 1.3 }}>
              {session?.name || 'Shared Session'}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1, width: '100%' }}>
              {session?.isOwner ? (
                starCount > 0 && (
                  <Chip
                    icon={<StarIcon sx={{ fontSize: '16px !important', color: 'warning.main' }} />}
                    label={starCount}
                    size="small"
                    variant="outlined"
                    sx={{ height: 36 }}
                  />
                )
              ) : (
                <Button
                  variant="outlined"
                  startIcon={isStarred ? <StarIcon /> : <StarBorderIcon />}
                  onClick={handleToggleStar}
                  disabled={starLoading}
                  sx={{
                    whiteSpace: 'nowrap',
                    ...(isStarred && {
                      borderColor: 'warning.main',
                      color: 'warning.main',
                      '&:hover': { borderColor: 'warning.dark', bgcolor: 'warning.50' }
                    })
                  }}
                >
                  {starCount}
                </Button>
              )}
              {!session?.isOwner && (
                <Button
                  variant="outlined"
                  startIcon={forking ? <CircularProgress size={16} /> : <CallSplitIcon />}
                  onClick={forkSession}
                  disabled={forking}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Fork{session?.forkCount > 0 ? ` (${session.forkCount})` : ''}
                </Button>
              )}
              {session?.isOwner && tabs.length >= 2 && (
                <>
                  {/* Session description: button + style toggle grouped */}
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, py: 0.25 }}>
                    <Button
                      variant="outlined"
                      startIcon={generatingDescription ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                      onClick={handleGenerateSessionDescription}
                      disabled={generatingDescription}
                      size="small"
                      sx={{ whiteSpace: 'nowrap', border: 'none', '&:hover': { border: 'none' } }}
                    >
                      {generatingDescription ? 'Generating...' : (session?.description ? 'Regen Session' : 'AI Session')}
                    </Button>
                    <ToggleButtonGroup
                      value={descriptionStyle}
                      exclusive
                      onChange={(e, val) => val && setDescriptionStyle(val)}
                      size="small"
                      sx={{ height: 30 }}
                    >
                      <Tooltip title="1-2 sentences, concise"><ToggleButton value="brief" sx={{ textTransform: 'none', fontSize: '0.7rem', px: 1 }}>Brief</ToggleButton></Tooltip>
                      <Tooltip title="3-5 sentences, detailed"><ToggleButton value="detailed" sx={{ textTransform: 'none', fontSize: '0.7rem', px: 1 }}>Detailed</ToggleButton></Tooltip>
                      <Tooltip title="Blog-style narrative"><ToggleButton value="editorial" sx={{ textTransform: 'none', fontSize: '0.7rem', px: 1 }}>Editorial</ToggleButton></Tooltip>
                    </ToggleButtonGroup>
                  </Stack>
                  {/* Tab description: button + style toggle grouped */}
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, py: 0.25 }}>
                    <Button
                      variant="outlined"
                      startIcon={generatingTabDescriptions ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                      onClick={handleGenerateTabDescriptions}
                      disabled={generatingTabDescriptions}
                      size="small"
                      sx={{ whiteSpace: 'nowrap', border: 'none', '&:hover': { border: 'none' } }}
                    >
                      {generatingTabDescriptions ? 'Generating...' : 'AI Tabs'}
                    </Button>
                    <ToggleButtonGroup
                      value={tabDescriptionStyle}
                      exclusive
                      onChange={(e, val) => val && setTabDescriptionStyle(val)}
                      size="small"
                      sx={{ height: 30 }}
                    >
                      <Tooltip title="~150 chars, 1 sentence"><ToggleButton value="brief" sx={{ textTransform: 'none', fontSize: '0.7rem', px: 1 }}>Brief</ToggleButton></Tooltip>
                      <Tooltip title="~300 chars, 2-3 sentences"><ToggleButton value="detailed" sx={{ textTransform: 'none', fontSize: '0.7rem', px: 1 }}>Detailed</ToggleButton></Tooltip>
                      <Tooltip title="~500 chars, narrative"><ToggleButton value="editorial" sx={{ textTransform: 'none', fontSize: '0.7rem', px: 1 }}>Editorial</ToggleButton></Tooltip>
                    </ToggleButtonGroup>
                  </Stack>
                </>
              )}
              {tabs.length > 0 && (
                <Button
                  variant="outlined"
                  startIcon={<OpenAllIcon />}
                  onClick={openAllTabs}
                  fullWidth={false}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {tabs.length > MAX_OPEN_TABS
                    ? `Open First ${MAX_OPEN_TABS}`
                    : `Open All (${tabs.length})`}
                </Button>
              )}
              {currentUser ? (
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={cloneSession}
                  disabled={cloning}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {cloning ? 'Saving...' : 'Save to Blackmount'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => navigate('/login?next=' + encodeURIComponent(window.location.pathname))}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Sign up to save
                </Button>
              )}

              {/* View mode toggle */}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, val) => { if (val) { setViewMode(val); setEditingTabId(null); } }}
                size="small"
                sx={{ height: 36 }}
              >
                <Tooltip title="Card view">
                  <ToggleButton value="cards">
                    <ViewModuleIcon fontSize="small" />
                  </ToggleButton>
                </Tooltip>
                <Tooltip title="Markdown view">
                  <ToggleButton value="markdown">
                    <CodeIcon fontSize="small" />
                  </ToggleButton>
                </Tooltip>
              </ToggleButtonGroup>

              {/* Issue 6: Font mode toggle (serif/sans) */}
              {viewMode === 'markdown' && (
                <ToggleButtonGroup
                  value={fontMode}
                  exclusive
                  onChange={(e, val) => val && setFontMode(val)}
                  size="small"
                  sx={{ height: 36 }}
                >
                  <Tooltip title="Sans-serif (Inter)">
                    <ToggleButton value="sans" sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1.5, fontFamily: 'Inter, sans-serif' }}>Aa</ToggleButton>
                  </Tooltip>
                  <Tooltip title="Serif (Lora)">
                    <ToggleButton value="serif" sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1.5, fontFamily: 'Lora, serif', fontStyle: 'italic' }}>Aa</ToggleButton>
                  </Tooltip>
                </ToggleButtonGroup>
              )}

              {/* Export kebab menu */}
              <Tooltip title="Export">
                <IconButton onClick={(e) => setExportMenuAnchor(e.currentTarget)} size="small">
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={() => setExportMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={handleCopyMarkdown}>
                  <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Copy Markdown</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDownloadMarkdown}>
                  <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Download .md</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleSaveAsNote} disabled={savingNote}>
                  <ListItemIcon><NoteAddIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{currentUser ? (savingNote ? 'Saving...' : 'Save as Note') : 'Sign in to save'}</ListItemText>
                </MenuItem>
              </Menu>
            </Stack>
          </Stack>

          {/* Description — editable for owner (Issue 4) */}
          {session?.isOwner ? (
            editingDescription ? (
              <Box sx={{ mb: 2 }}>
                <TextField
                  multiline
                  fullWidth
                  minRows={2}
                  maxRows={6}
                  value={descDraft}
                  onChange={(e) => setDescDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveDescription(); }
                    if (e.key === 'Escape') { setEditingDescription(false); }
                  }}
                  autoFocus
                  placeholder="Add a description..."
                  size="small"
                  sx={{ mb: 1 }}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={savingDescription ? <CircularProgress size={14} /> : <CheckIcon />}
                    onClick={handleSaveDescription}
                    disabled={savingDescription}
                    sx={{ textTransform: 'none' }}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={() => setEditingDescription(false)}
                    sx={{ textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Box
                onClick={() => { setDescDraft(session?.description || ''); setEditingDescription(true); }}
                sx={{
                  mb: 2,
                  cursor: 'pointer',
                  position: 'relative',
                  '&:hover .edit-icon': { opacity: 1 },
                  borderRadius: 1,
                  p: session?.description ? 0 : 1.5,
                  border: session?.description ? 'none' : '1px dashed',
                  borderColor: 'divider'
                }}
              >
                {session?.description && session.description.trim() !== (session.name || '').trim() ? (
                  isCuratedList ? (
                    <Box sx={{ color: 'text.secondary', lineHeight: 1.6, pr: 3 }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={inlineMarkdownComponents}>
                        {session.description}
                      </ReactMarkdown>
                    </Box>
                  ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6, pr: 3 }}>
                      {session.description}
                    </Typography>
                  )
                ) : (
                  <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                    Click to add a description...
                  </Typography>
                )}
                <EditIcon
                  className="edit-icon"
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    fontSize: 16,
                    color: 'text.secondary',
                    opacity: 0,
                    transition: 'opacity 0.15s ease'
                  }}
                />
              </Box>
            )
          ) : (
            session?.description && session.description.trim() !== (session.name || '').trim() && (
              isCuratedList ? (
                <Box sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.6 }}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={inlineMarkdownComponents}>
                    {session.description}
                  </ReactMarkdown>
                </Box>
              ) : (
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                  {session.description}
                </Typography>
              )
            )
          )}

          {/* Owner Attribution */}
          {owner ? (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              mb: 2,
              p: 1.25,
              bgcolor: 'action.hover',
              borderRadius: 1.5
            }}>
              <Avatar
                src={owner.profilePictureUrl}
                alt={owner.displayName}
                sx={{ width: 28, height: 28, fontSize: '0.85rem' }}
              />
              <Typography variant="body2" color="text.secondary">
                Curated by{' '}
                <Link
                  to={`/u/${owner.username}`}
                  style={{ fontWeight: 'bold', color: theme.palette.primary.main, textDecoration: 'none' }}
                  onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                >
                  @{owner.username}
                </Link>
              </Typography>
              {owner.joinDate && (
                <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                  Member since {new Date(owner.joinDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Curated on Blackmount
            </Typography>
          )}

          {/* Forked-from attribution banner */}
          {session?.forkedFrom?.shareToken && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
              p: 1.25,
              bgcolor: 'action.hover',
              borderRadius: 1.5,
              flexWrap: 'wrap'
            }}>
              <CallSplitIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Forked from{' '}
                {session.forkedFrom.username && (
                  <>
                    <Link
                      to={`/u/${session.forkedFrom.username}`}
                      style={{ fontWeight: 'bold', color: theme.palette.primary.main, textDecoration: 'none' }}
                      onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                    >
                      @{session.forkedFrom.username}
                    </Link>
                    {"'s "}
                  </>
                )}
                {session.forkedFrom.shareToken ? (
                  <Link
                    to={`/shared/session/${session.forkedFrom.shareToken}`}
                    style={{ fontWeight: 500, color: theme.palette.primary.main, textDecoration: 'none' }}
                    onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                  >
                    {session.forkedFrom.sessionTitle || 'original session'}
                  </Link>
                ) : (
                  <span style={{ fontWeight: 500 }}>
                    {session.forkedFrom.sessionTitle || 'original session'}
                  </span>
                )}
              </Typography>
              {session.forkedFrom.shareToken && (
                <Button
                  size="small"
                  startIcon={loadingDiff ? <CircularProgress size={14} /> : <CompareArrowsIcon />}
                  onClick={loadDiff}
                  disabled={loadingDiff}
                  sx={{ ml: 'auto', textTransform: 'none', fontSize: '0.75rem' }}
                >
                  {showDiff ? 'Hide Changes' : 'View Changes'}
                </Button>
              )}
            </Box>
          )}

          {/* Import source attribution banner */}
          {session?.importSource?.url && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
              p: 1.25,
              bgcolor: 'action.hover',
              borderRadius: 1.5,
              flexWrap: 'wrap'
            }}>
              <LinkIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Imported from{' '}
                <a
                  href={session.importSource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontWeight: 500, color: theme.palette.primary.main, textDecoration: 'none' }}
                  onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                >
                  {session.importSource.pageTitle || (() => {
                    try { return new URL(session.importSource.url).hostname; } catch { return 'external page'; }
                  })()}
                </a>
              </Typography>
              <Chip
                label={
                  session.importSource.type === 'github-readme' ? 'GitHub' :
                  session.importSource.type === 'markdown' ? 'Markdown' : 'Web'
                }
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
              />
              {session.importSource.linkCount > 0 && (
                <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
                  {session.importSource.linkCount} links organized
                </Typography>
              )}
            </Box>
          )}

          {/* Diff view */}
          <Collapse in={showDiff && !!diffResult}>
            {diffResult && (
              <Box sx={{ mb: 2 }}>
                {diffResult.added.length === 0 && diffResult.removed.length === 0 && diffResult.edited.length === 0 && (
                  <Alert severity="info" sx={{ mb: 1 }}>No changes from the original session.</Alert>
                )}
                {diffResult.added.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ color: 'success.main', mb: 0.5 }}>
                      + {diffResult.added.length} added
                    </Typography>
                    {diffResult.added.map((tab, i) => (
                      <Box key={i} sx={{ pl: 2, py: 0.5, borderLeft: 3, borderColor: 'success.main', mb: 0.5, bgcolor: 'rgba(46,125,50,0.04)', borderRadius: '0 4px 4px 0' }}>
                        <Typography variant="body2" fontWeight={500}>{tab.title || tab.url}</Typography>
                        <Typography variant="caption" color="text.secondary">{tab.hostname}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                {diffResult.removed.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ color: 'error.main', mb: 0.5 }}>
                      - {diffResult.removed.length} removed
                    </Typography>
                    {diffResult.removed.map((tab, i) => (
                      <Box key={i} sx={{ pl: 2, py: 0.5, borderLeft: 3, borderColor: 'error.main', mb: 0.5, bgcolor: 'rgba(211,47,47,0.04)', borderRadius: '0 4px 4px 0' }}>
                        <Typography variant="body2" fontWeight={500} sx={{ textDecoration: 'line-through' }}>{tab.title || tab.url}</Typography>
                        <Typography variant="caption" color="text.secondary">{tab.hostname}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                {diffResult.edited.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ color: 'warning.main', mb: 0.5 }}>
                      ~ {diffResult.edited.length} edited
                    </Typography>
                    {diffResult.edited.map(({ current, parent }, i) => (
                      <Box key={i} sx={{ pl: 2, py: 0.5, borderLeft: 3, borderColor: 'warning.main', mb: 0.5, bgcolor: 'rgba(237,108,2,0.04)', borderRadius: '0 4px 4px 0' }}>
                        <Typography variant="body2" fontWeight={500}>{current.title || current.url}</Typography>
                        {(current.description || '') !== (parent.description || '') && (
                          <Box sx={{ mt: 0.25 }}>
                            {parent.description && (
                              <Typography variant="caption" sx={{ color: 'error.main', textDecoration: 'line-through', display: 'block' }}>
                                {parent.description}
                              </Typography>
                            )}
                            {current.description && (
                              <Typography variant="caption" sx={{ color: 'success.main', display: 'block' }}>
                                {current.description}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Collapse>

          {/* Tags */}
          {session?.tags?.length > 0 && (
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
              {session.tags.map(tag => (
                <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
              ))}
            </Stack>
          )}

          {/* Stats row */}
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Chip
              icon={<TabIcon sx={{ fontSize: '16px !important' }} />}
              label={`${tabs.length} tab${tabs.length !== 1 ? 's' : ''}`}
              size="small"
              variant="outlined"
            />
            <Chip
              icon={<DomainIcon sx={{ fontSize: '16px !important' }} />}
              label={`${uniqueDomains} domain${uniqueDomains !== 1 ? 's' : ''}`}
              size="small"
              variant="outlined"
            />
            {session?.forkCount > 0 && (
              <Chip
                icon={<CallSplitIcon sx={{ fontSize: '16px !important' }} />}
                label={`${session.forkCount} fork${session.forkCount !== 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
              />
            )}
            {session?.createdAt && (
              <Chip
                icon={<CalendarIcon sx={{ fontSize: '16px !important' }} />}
                label={formatDate(session.createdAt)}
                size="small"
                variant="outlined"
              />
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Empty state — shown for both view modes */}
      {tabs.length === 0 ? (
        <Card sx={{ overflow: 'hidden', '&:hover': { transform: 'none' } }}>
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <TabIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Tabs in This Session
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This session doesn't contain any tabs yet.
            </Typography>
          </Box>
        </Card>
      ) : viewMode === 'markdown' ? (
        /* ── Rendered markdown view (Issue 3: skipHeader, Issue 6: font mode) ── */
        <Card sx={{ overflow: 'hidden', '&:hover': { transform: 'none' } }}>
          <CardContent sx={{
            p: { xs: 2, md: 3 },
            fontFamily: fontMode === 'serif' ? 'Lora, serif' : 'Inter, sans-serif'
          }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: theme.palette.primary.main }}>
                    {children}
                  </a>
                ),
                h1: ({ children }) => <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontFamily: 'inherit' }}>{children}</Typography>,
                h2: ({ children }) => <Typography variant="h5" fontWeight={600} sx={{ mt: 3, mb: 1.5, fontFamily: 'inherit' }}>{children}</Typography>,
                p: ({ children }) => <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7, fontFamily: 'inherit' }}>{children}</Typography>,
                li: ({ children }) => <li style={{ marginBottom: 4, lineHeight: 1.6 }}>{children}</li>,
                blockquote: ({ children }) => (
                  <Box sx={{ borderLeft: 3, borderColor: 'primary.main', pl: 2, my: 1.5, color: 'text.secondary' }}>
                    {children}
                  </Box>
                ),
              }}
            >
              {markdownForView}
            </ReactMarkdown>
          </CardContent>
        </Card>
      ) : hasSections ? (
        /* ── Section-based view (curated sessions with sections) ── */
        <Stack spacing={2.5}>
          {/* Search bar for large lists */}
          {tabs.length > 50 && (
            <TextField
              size="small"
              placeholder={`Search ${tabs.length} links...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <DomainIcon sx={{ mr: 1, color: 'text.disabled', fontSize: 18 }} />,
                endAdornment: searchQuery && (
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )
              }}
              sx={{ maxWidth: 400 }}
            />
          )}
          {searchQuery && (
            <Typography variant="body2" color="text.secondary">
              Showing {filteredTabs.length} of {tabs.length} links
            </Typography>
          )}
          {/* Expand/Collapse all toggle for curated lists */}
          {isCuratedList && sectionGroups.length > 3 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => {
                  if (collapsedSections.size > 0) {
                    setCollapsedSections(new Set());
                  } else {
                    setCollapsedSections(new Set(sectionGroups.map(g => g.id)));
                  }
                }}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                {collapsedSections.size > 0 ? 'Expand All' : 'Collapse All'}
              </Button>
            </Box>
          )}
          {sectionGroups.map((group) => {
            const isCollapsed = collapsedSections.has(group.id);
            const toggleCollapse = () => {
              setCollapsedSections(prev => {
                const next = new Set(prev);
                if (next.has(group.id)) next.delete(group.id);
                else next.add(group.id);
                return next;
              });
            };
            return (
            <Box key={group.id}>
              {/* Section header — clickable for collapse */}
              <Box
                onClick={toggleCollapse}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: isCollapsed ? 0 : 1.5,
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { opacity: 0.8 }
                }}
              >
                {isCuratedList && (
                  isCollapsed
                    ? <ExpandMoreIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    : <ExpandLessIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                )}
                <Typography variant="h6" fontWeight={600} sx={{ color: 'text.primary' }}>
                  {group.title}
                </Typography>
                <Divider sx={{ flex: 1 }} />
                <Chip
                  label={group.tabs.length + (group.secondaryTabs?.length || 0)}
                  size="small"
                  sx={{
                    height: 22,
                    minWidth: 28,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText'
                  }}
                />
              </Box>

              {/* Rich link cards — collapsible for curated lists */}
              <Collapse in={!isCollapsed}>
              <Stack spacing={1}>
                {group.tabs.map((tab, tabIndex) => (
                  <Card
                    key={`${tab.url}-${tabIndex}`}
                    component="a"
                    href={tab.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'block',
                      textDecoration: 'none',
                      color: 'inherit',
                      overflow: 'hidden',
                      transition: 'box-shadow 0.15s ease, transform 0.15s ease',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-1px)',
                        '& .external-icon': { opacity: 1 }
                      }
                    }}
                  >
                    <Box sx={{ px: 2.5, py: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        {/* Favicon */}
                        <img
                          src={tab.favicon || getFaviconUrl(tab.hostname)}
                          alt=""
                          style={{ width: 16, height: 16, borderRadius: 2, flexShrink: 0, marginTop: 3 }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />

                        {/* Content */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {/* Title + external icon */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ flex: 1, color: 'text.primary' }}>
                              {tab.title || 'Untitled'}
                            </Typography>
                            <ExternalIcon
                              className="external-icon"
                              sx={{
                                fontSize: 14,
                                color: 'text.secondary',
                                opacity: 0,
                                transition: 'opacity 0.15s ease',
                                flexShrink: 0
                              }}
                            />
                          </Box>

                          {/* Hostname */}
                          <Typography variant="caption" color="text.secondary">
                            {tab.hostname}
                          </Typography>

                          {/* Description — inline editable for owner */}
                          {session?.isOwner ? (
                            editingTabId === tab.tabId ? (
                              <Box
                                sx={{ mt: 0.5 }}
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                              >
                                <TextField
                                  multiline
                                  fullWidth
                                  minRows={1}
                                  maxRows={4}
                                  value={tabDescDraft}
                                  onChange={(e) => setTabDescDraft(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveTabDescription(tab.tabId); }
                                    if (e.key === 'Escape') { setEditingTabId(null); }
                                  }}
                                  autoFocus
                                  placeholder="Add a description..."
                                  size="small"
                                  inputProps={{ maxLength: 1000 }}
                                  sx={{ mb: 0.5, '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                                />
                                <Stack direction="row" spacing={0.5}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleSaveTabDescription(tab.tabId); }}
                                    disabled={savingTabDesc}
                                    color="primary"
                                  >
                                    {savingTabDesc ? <CircularProgress size={14} /> : <CheckIcon fontSize="small" />}
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingTabId(null); }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Box>
                            ) : (
                              <Box
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!tab.tabId) return;
                                  setTabDescDraft(tab.description || '');
                                  setEditingTabId(tab.tabId);
                                }}
                                sx={{
                                  mt: 0.5,
                                  cursor: 'pointer',
                                  position: 'relative',
                                  '&:hover .tab-edit-icon': { opacity: 1 },
                                  borderRadius: 0.5,
                                  ...(!(tab.description && tab.description.trim() !== (tab.title || '').trim()) && {
                                    p: 1,
                                    border: '1px dashed',
                                    borderColor: 'divider'
                                  })
                                }}
                              >
                                {tab.description && tab.description.trim() !== (tab.title || '').trim() ? (
                                  <TabDescription tab={tab} sx={{ pr: 2.5 }} />
                                ) : (
                                  <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                    Click to add description...
                                  </Typography>
                                )}
                                <EditIcon
                                  className="tab-edit-icon"
                                  sx={{
                                    position: 'absolute',
                                    top: 2,
                                    right: 2,
                                    fontSize: 14,
                                    color: 'text.secondary',
                                    opacity: 0,
                                    transition: 'opacity 0.15s ease'
                                  }}
                                />
                              </Box>
                            )
                          ) : (
                            <TabDescription tab={tab} sx={{ mt: 0.5 }} />
                          )}

                          {/* Source tag + metadata row */}
                          {((tab.sourceTag && !tab.sourceTag.startsWith('imported')) || tab.metadata) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                              {tab.sourceTag && !tab.sourceTag.startsWith('imported') && (
                                <Chip
                                  label={tab.sourceTag}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                              {tab.metadata && (
                                <Typography variant="caption" color="text.disabled">
                                  {tab.metadata}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Card>
                ))}
              </Stack>
              {/* Secondary links — hidden by default */}
              {group.secondaryTabs?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    onClick={() => setShowSecondary(prev => ({ ...prev, [group.id]: !prev[group.id] }))}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary' }}
                    endIcon={showSecondary[group.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  >
                    Related ({group.secondaryTabs.length})
                  </Button>
                  <Collapse in={showSecondary[group.id]}>
                    <Stack spacing={0.5} sx={{ mt: 0.5, pl: 2 }}>
                      {group.secondaryTabs.map((tab, i) => (
                        <Box
                          key={`sec-${tab.url}-${i}`}
                          component="a"
                          href={tab.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.25,
                            textDecoration: 'none',
                            color: 'text.secondary',
                            fontSize: '0.8rem',
                            '&:hover': { color: 'primary.main' }
                          }}
                        >
                          <img
                            src={tab.favicon || getFaviconUrl(tab.hostname)}
                            alt=""
                            style={{ width: 12, height: 12, borderRadius: 1, flexShrink: 0 }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                            {tab.title || tab.hostname}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Collapse>
                </Box>
              )}
              </Collapse>
            </Box>
          );
          })}
        </Stack>
      ) : (
        /* ── Domain-grouped view (default, unchanged) ── */
        <Stack spacing={1.5}>
          {domainGroups.map(([domain, domainTabs], groupIndex) => {
            const isCollapsed = collapsedDomains[domain] || false;
            const firstFavicon = domainTabs[0]?.favicon;

            return (
              <Card
                key={domain}
                sx={{
                  overflow: 'hidden',
                  bgcolor: groupIndex % 2 === 0 ? 'background.paper' : 'action.hover',
                  '&:hover': { transform: 'none' }
                }}
              >
                {/* Domain header */}
                <Box
                  onClick={() => toggleDomain(domain)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 2.5,
                    py: 1.5,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { bgcolor: 'action.hover' },
                    transition: 'background-color 0.15s ease'
                  }}
                >
                  <img
                    src={firstFavicon || getFaviconUrl(domain)}
                    alt=""
                    style={{ width: 16, height: 16, borderRadius: 2, flexShrink: 0 }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1, color: 'text.primary' }}>
                    {domain}
                  </Typography>
                  <Chip
                    label={domainTabs.length}
                    size="small"
                    sx={{
                      height: 22,
                      minWidth: 28,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText'
                    }}
                  />
                  {isCollapsed ? <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} /> : <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
                </Box>

                {/* Tab list within domain */}
                <Collapse in={!isCollapsed}>
                  <List disablePadding sx={{ px: 0.5, pb: 0.5 }}>
                    {domainTabs.map((tab, tabIndex) => (
                      <ListItem
                        key={`${tab.url}-${tabIndex}`}
                        disablePadding
                        sx={{ borderRadius: 1.5, mx: 0.5, mb: 0.25 }}
                      >
                        {session?.isOwner && editingTabId === tab.tabId ? (
                          /* Inline edit mode — replaces the link button */
                          <Box sx={{ py: 1, px: 2, width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <img
                                src={tab.favicon || getFaviconUrl(tab.hostname)}
                                alt=""
                                style={{ width: 16, height: 16, borderRadius: 2, flexShrink: 0 }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <Typography variant="body2" fontWeight={500} noWrap>
                                {tab.title || 'Untitled'}
                              </Typography>
                            </Box>
                            <TextField
                              multiline
                              fullWidth
                              minRows={1}
                              maxRows={3}
                              value={tabDescDraft}
                              onChange={(e) => setTabDescDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveTabDescription(tab.tabId); }
                                if (e.key === 'Escape') { setEditingTabId(null); }
                              }}
                              autoFocus
                              placeholder="Add a description..."
                              size="small"
                              inputProps={{ maxLength: 1000 }}
                              sx={{ mb: 0.5, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                            />
                            <Stack direction="row" spacing={0.5}>
                              <IconButton
                                size="small"
                                onClick={() => handleSaveTabDescription(tab.tabId)}
                                disabled={savingTabDesc}
                                color="primary"
                              >
                                {savingTabDesc ? <CircularProgress size={14} /> : <CheckIcon fontSize="small" />}
                              </IconButton>
                              <IconButton size="small" onClick={() => setEditingTabId(null)}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Box>
                        ) : (
                          <ListItemButton
                            component="a"
                            href={tab.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              py: 1,
                              px: 2,
                              borderRadius: 1.5,
                              '&:hover .external-icon': { opacity: 1 },
                              '&:hover .tab-edit-btn': { opacity: 1 }
                            }}
                          >
                            {/* Issue 5: Per-tab favicon */}
                            <img
                              src={tab.favicon || getFaviconUrl(tab.hostname)}
                              alt=""
                              style={{ width: 16, height: 16, borderRadius: 2, flexShrink: 0, marginRight: 10, marginTop: 2 }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <ListItemText
                              primary={
                                <Typography variant="body2" fontWeight={500} noWrap sx={{ pr: 3 }}>
                                  {tab.title || 'Untitled'}
                                </Typography>
                              }
                              secondary={
                                session?.isOwner ? (
                                  (tab.description && tab.description.trim() !== (tab.title || '').trim())
                                    ? tab.description
                                    : <span style={{ fontStyle: 'italic', color: 'inherit', opacity: 0.5 }}>Click pencil to add description...</span>
                                ) : (
                                  (tab.description && tab.description.trim() !== (tab.title || '').trim()) ? tab.description : undefined
                                )
                              }
                              secondaryTypographyProps={{
                                variant: 'caption',
                                sx: {
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }
                              }}
                            />
                            {session?.isOwner && (
                              <IconButton
                                className="tab-edit-btn"
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!tab.tabId) return;
                                  setTabDescDraft(tab.description || '');
                                  setEditingTabId(tab.tabId);
                                }}
                                sx={{ opacity: 0, transition: 'opacity 0.15s ease', mr: 0.5 }}
                              >
                                <EditIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            )}
                            <ExternalIcon
                              className="external-icon"
                              sx={{
                                fontSize: 16,
                                color: 'text.secondary',
                                opacity: 0,
                                transition: 'opacity 0.15s ease',
                                flexShrink: 0
                              }}
                            />
                          </ListItemButton>
                        )}
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Footer */}
      <Box sx={{ mt: 5, textAlign: 'center', pb: 2 }}>
        {/* Extension install nudge */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
          <ExtensionIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Save your own browser sessions in one click
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          color="primary"
          href={getExtensionUrl(detectBrowser())}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<ExtensionIcon />}
          sx={{ mb: 3, textTransform: 'none' }}
        >
          Install for {getBrowserLabel(detectBrowser())}
        </Button>

        <Box
          component="img"
          src="/blackmount-logo.jpg"
          alt="Blackmount"
          sx={{ width: 32, height: 32, borderRadius: '50%', mb: 1, opacity: 0.7 }}
        />
        <Typography variant="body2" color="text.secondary">
          <a href="/" style={{ color: 'inherit', textDecoration: 'none', fontWeight: 500 }}>
            Create your own sessions on Blackmount
          </a>
        </Typography>
      </Box>

      {/* Snackbar for clone feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.message === 'already_forked' ? 8000 : 4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message === 'session_saved' ? (
            <>
              Session saved!{' '}
              <a
                href={getExtensionUrl(detectBrowser())}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', fontWeight: 600 }}
              >
                Install the extension
              </a>{' '}
              to save your own browser tabs
            </>
          ) : snackbar.message === 'session_forked' ? (
            <>
              Session forked to your library!{' '}
              <a
                href="/sessions"
                style={{ color: 'inherit', fontWeight: 600 }}
              >
                View in Sessions
              </a>
            </>
          ) : snackbar.message === 'already_forked' ? (
            <>
              You already forked this session.{' '}
              <a
                href={snackbar.forkId ? `/sessions?highlight=${snackbar.forkId}` : '/sessions'}
                style={{ color: 'inherit', fontWeight: 600 }}
              >
                Go to your fork
              </a>
            </>
          ) : (
            snackbar.message
          )}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SharedSession;
